import { useState } from "react";
import { Pencil, Plus, X, Check } from "lucide-react";

const KEY = "sb_pos_quick_prices";
const DEFAULTS = [5, 10, 20, 50, 100, 200];

export function loadQuickPrices() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY));
    if (Array.isArray(raw) && raw.length) return raw.map(Number).filter(Boolean);
  } catch (_e) {
    /* fall through to defaults */
  }
  return DEFAULTS;
}

export default function QuickPrices({ prices, setPrices, onPick }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const persist = (next) => {
    setPrices(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const add = () => {
    const v = Math.round(Number(draft));
    if (!v || v <= 0) return;
    if (prices.includes(v)) {
      setDraft("");
      return;
    }
    persist([...prices, v].sort((a, b) => a - b));
    setDraft("");
  };

  return (
    <div
      data-testid="quick-prices"
      className="rounded-[4px] bg-white border-2 border-slate-200 p-3"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
          Quick prices
        </span>
        <button
          type="button"
          data-testid="quick-prices-edit-toggle"
          onClick={() => setEditing((v) => !v)}
          className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#0e6ea6]"
        >
          {editing ? <Check size={13} /> : <Pencil size={12} />}
          {editing ? "Done" : "Edit"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {prices.map((p) => (
          <div key={p} className="relative">
            <button
              type="button"
              data-testid={`quick-price-${p}`}
              onClick={() => !editing && onPick(p)}
              className="h-12 min-w-[72px] px-3 rounded-[4px] bg-slate-100 hover:bg-slate-900 hover:text-white border-2 border-slate-200 font-black text-lg tabular-nums text-slate-900 transition-colors"
            >
              ₹{p}
            </button>
            {editing && (
              <button
                type="button"
                data-testid={`quick-price-remove-${p}`}
                onClick={() => persist(prices.filter((x) => x !== p))}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#b91c1c] text-white flex items-center justify-center shadow"
                aria-label={`Remove ${p}`}
              >
                <X size={13} strokeWidth={3} />
              </button>
            )}
          </div>
        ))}

        {editing && (
          <div className="flex items-center gap-1">
            <input
              data-testid="quick-price-new-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              type="number"
              inputMode="numeric"
              placeholder="₹"
              className="h-12 w-20 px-2 rounded-[4px] border-2 border-slate-300 focus:border-[#299fe0] outline-none font-bold text-slate-900 text-center"
            />
            <button
              type="button"
              data-testid="quick-price-add"
              onClick={add}
              className="h-12 w-12 rounded-[4px] bg-[#15803d] text-white flex items-center justify-center"
              aria-label="Add quick price"
            >
              <Plus size={20} strokeWidth={3} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
